import { useQuery } from '@tanstack/react-query';

import { AgeRatingDto } from '@cineamo/api/models/age-rating/AgeRatingDto.types';
import { MovieDto } from '@cineamo/api/models/movie/v2/MovieDto.types';
import { MovieAgeRatingDto } from '@cineamo/api/models/movie-age-rating/MovieAgeRatingDto.types';

import { CACHE_24H } from '@cineamo/api/constants/caching-constants';

import { getAgeRating } from '@cineamo/api/resources/age-rating/age-rating-api';
import { getMovieAgeRatings } from '@cineamo/api/resources/movie/movie-api';

import { ISO_3166_2_LOCALE } from './locale-helper';

export async function getAgeRatingForISO_3166_2(
    movieId: MovieDto['id'],
    iso31662: ISO_3166_2_LOCALE
): Promise<AgeRatingDto> {
    const ageRatings = await getMovieAgeRatings(movieId, {
        iso31662s: [iso31662]
    });
    // We can take the first age rating for the given ISO_3166_2 because the API guarantees that there is only one age rating
    //  per Movie per ISO_3166_2.
    const movieAgeRating: MovieAgeRatingDto = ageRatings?._embedded?.['movie-age-ratings']?.[0] || null;
    if (!movieAgeRating) {
        return null;
    }
    return await getAgeRating(movieAgeRating.ageRatingId);
}

export const UseGetAgeRatingForISO_3166_2_QueryKey = 'age-rating-for-iso-3166-2';

export function useGetAgeRatingForISO_3166_2(options: {
    movieId: MovieDto['id'];
    iso31662: ISO_3166_2_LOCALE;
    enabled?: boolean;
}): {
    ageRating: AgeRatingDto;
    isLoading: boolean;
} {
    const { movieId, iso31662 } = options;
    const { data: ageRating, isLoading } = useQuery({
        queryKey: [UseGetAgeRatingForISO_3166_2_QueryKey, movieId, iso31662],
        queryFn: async () => getAgeRatingForISO_3166_2(movieId, iso31662),
        enabled: options.enabled !== false && !!movieId && !!iso31662,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        staleTime: CACHE_24H // age ratings rarely change — cache for 24h like the mobile app
    });
    return {
        ageRating: ageRating,
        isLoading: isLoading
    };
}
